class VariantAvailability {
  constructor() {
    this.isProcessing = false;
    this.initialize();
  }

  initialize() {
    this.isProcessing = true;

    this.filterUnavailableOptions();
    setTimeout(() => this.filterUnavailableOptions(), 500);

    this.waitForVariantData().then(() => {
      this.filterUnavailableOptions();
      this.setupEventListeners();
      this.setupMutationObserver();
      this.filterProductDataForTheme();
      this.ensureThemeCompatibility();
      this.ensureProperInitialVariantSelection();
      this.coordinateWithDropdownLegendHandler();
      this.preventToStringError();

      this.isProcessing = false;
    });
  }

  waitForVariantData() {
    return new Promise((resolve) => {
      const checkVariantData = () => {
        const variantData = document.getElementById('product-options-variant-data');
        if (variantData) {
          resolve();
        } else {
          setTimeout(checkVariantData, 100);
        }
      };
      checkVariantData();
    });
  }

  // Method to get current selected variant
  getCurrentSelectedVariant() {
    const variantIdInput = document.querySelector('input[name="id"], select[name="id"]');
    if (variantIdInput && variantIdInput.value) {
      const variantDataScript = document.getElementById('product-options-variant-data');
      if (variantDataScript) {
        try {
          const productData = JSON.parse(variantDataScript.textContent);
          return productData.variants.find(v => v.id.toString() === variantIdInput.value.toString());
        } catch (e) {
          // Error getting current variant
        }
      }
    }
    return null;
  }

  // New method to filter product data for theme compatibility
  filterProductDataForTheme() {
    const variantDataScript = document.getElementById('product-options-variant-data');
    if (!variantDataScript) return;

    try {
      const productData = JSON.parse(variantDataScript.textContent);

      // Create a filtered version of the product data that excludes unavailable variants.
      // Always keep the variant the user navigated to (from the PLP link) so that
      // variant-picker.js can find it on load — even if its availability metafield is false.
      const urlVariantId = window.__INITIAL_VARIANT_FROM_URL
        ? parseInt(window.__INITIAL_VARIANT_FROM_URL, 10)
        : null;

      const filteredProductData = {
        ...productData,
        variants: productData.variants.filter((variant, idx) => {
          const metafields = productData.variants_metafields[idx];
          if (urlVariantId && variant.id === urlVariantId) return true; // always keep URL variant
          return !(
            (metafields && metafields.custom && metafields.custom.availability === false) ||
            (metafields && metafields.availability === false)
          );
        }),
        variants_metafields: productData.variants_metafields.filter((metafields, idx) => {
          const variant = productData.variants[idx];
          if (urlVariantId && variant && variant.id === urlVariantId) return true; // always keep URL variant
          return !(
            (metafields && metafields.custom && metafields.custom.availability === false) ||
            (metafields && metafields.availability === false)
          );
        })
      };

      // Update the script content with filtered data
      variantDataScript.textContent = JSON.stringify(filteredProductData);

      // Dispatch a custom event to notify other scripts that product data has been filtered
      document.dispatchEvent(new CustomEvent('product:data-filtered', {
        detail: { productData: filteredProductData }
      }));

      // Update theme's variant selection logic to work with filtered data
      this.updateThemeVariantSelection(filteredProductData);

    } catch (e) {
      // Error filtering product data
    }
  }

  // Method to update theme's variant selection logic
  updateThemeVariantSelection(filteredProductData) {
    // Find and update any existing product form instances
    const productForms = document.querySelectorAll('form[data-product-form]');
    productForms.forEach(form => {
      // If the form has a product data attribute, update it
      if (form.hasAttribute('data-product')) {
        try {
          const currentProductData = JSON.parse(form.getAttribute('data-product'));
          const updatedProductData = {
            ...currentProductData,
            variants: filteredProductData.variants
          };
          form.setAttribute('data-product', JSON.stringify(updatedProductData));
        } catch (e) {
          // Could not update form product data
        }
      }
    });

    // Update any global product variables that might be used by the theme
    if (window.product && window.product.variants) {
      window.product.variants = filteredProductData.variants;
    }

    // Trigger a custom event that the theme can listen to for updating variant selection
    document.dispatchEvent(new CustomEvent('variant:availability-updated', {
      detail: {
        productData: filteredProductData,
        originalVariants: this.getOriginalProductData()?.variants || []
      }
    }));
  }

  // Helper method to get original product data
  getOriginalProductData() {
    const variantDataScript = document.getElementById('product-options-variant-data');
    if (!variantDataScript) return null;

    try {
      // Try to get the original data from a backup or parse the current data
      return JSON.parse(variantDataScript.textContent);
    } catch (e) {
      return null;
    }
  }

  // Method to ensure theme's variant selection works with filtered data
  ensureThemeCompatibility() {
    // Override the theme's getVariant function to work with filtered data
    if (window.Shopify && window.Shopify.OptionSelectors) {
      const originalGetVariant = window.Shopify.OptionSelectors.prototype.getVariant;
      if (originalGetVariant) {
        window.Shopify.OptionSelectors.prototype.getVariant = function () {
          const variant = originalGetVariant.apply(this, arguments);
          if (variant) {
            // Check if this variant is available according to our filtering
            const variantDataScript = document.getElementById('product-options-variant-data');
            if (variantDataScript) {
              try {
                const productData = JSON.parse(variantDataScript.textContent);
                const variantIndex = productData.variants.findIndex(v => v.id === variant.id);
                if (variantIndex !== -1) {
                  const metafields = productData.variants_metafields[variantIndex];
                  if (
                    (metafields && metafields.custom && metafields.custom.availability === false) ||
                    (metafields && metafields.availability === false)
                  ) {
                    // Return null for unavailable variants
                    return null;
                  }
                }
              } catch (e) {
                // Error checking variant availability
              }
            }
          }
          return variant;
        };
      }
    }

    // Also override any global getVariant functions
    if (window.getVariantFromOptionArray) {
      const originalGetVariantFromOptionArray = window.getVariantFromOptionArray;
      window.getVariantFromOptionArray = function (product, options) {
        const variant = originalGetVariantFromOptionArray(product, options);
        if (variant) {
          // Check if this variant is available according to our filtering
          const variantDataScript = document.getElementById('product-options-variant-data');
          if (variantDataScript) {
            try {
              const productData = JSON.parse(variantDataScript.textContent);
              const variantIndex = productData.variants.findIndex(v => v.id === variant.id);
              if (variantIndex !== -1) {
                const metafields = productData.variants_metafields[variantIndex];
                if (
                  (metafields && metafields.custom && metafields.custom.availability === false) ||
                  (metafields && metafields.availability === false)
                ) {
                  // Return null for unavailable variants
                  return null;
                }
              }
            } catch (e) {
              // Error checking variant availability
            }
          }
        }
        return variant;
      };
    }

    // Override the theme's variant ID input update logic
    this.overrideVariantIdInput();
  }

  // Method to override variant ID input updates
  overrideVariantIdInput() {
    // Watch for changes to variant ID inputs
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'value') {
          const target = mutation.target;
          if (target.name === 'id' || target.matches('input[name="id"], select[name="id"]')) {
            const variantId = target.value;
            if (variantId) {
              // Check if this variant is available
              const variantDataScript = document.getElementById('product-options-variant-data');
              if (variantDataScript) {
                try {
                  const productData = JSON.parse(variantDataScript.textContent);
                  const variantIndex = productData.variants.findIndex(v => v.id.toString() === variantId.toString());
                  if (variantIndex !== -1) {
                    const metafields = productData.variants_metafields[variantIndex];
                    if (
                      (metafields && metafields.custom && metafields.custom.availability === false) ||
                      (metafields && metafields.availability === false)
                    ) {
                      // Find the first available variant
                      const availableVariant = productData.variants.find((v, idx) => {
                        const vMetafields = productData.variants_metafields[idx];
                        return !(
                          (vMetafields && vMetafields.custom && vMetafields.custom.availability === false) ||
                          (vMetafields && vMetafields.availability === false)
                        );
                      });

                      if (availableVariant) {
                        // Update the input with the available variant ID
                        target.value = availableVariant.id;
                      }
                    }
                  }
                } catch (e) {
                  // Error checking variant ID availability
                }
              }
            }
          }
        }
      });
    });

    // Observe all variant ID inputs
    const variantIdInputs = document.querySelectorAll('input[name="id"], select[name="id"]');
    variantIdInputs.forEach(input => {
      observer.observe(input, {
        attributes: true,
        attributeFilter: ['value']
      });
    });

    // Also observe for new inputs being added
    const bodyObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const newInputs = node.querySelectorAll ? node.querySelectorAll('input[name="id"], select[name="id"]') : [];
              newInputs.forEach(input => {
                observer.observe(input, {
                  attributes: true,
                  attributeFilter: ['value']
                });
              });
            }
          });
        }
      });
    });

    bodyObserver.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Also override variant selection dropdowns
    this.overrideVariantSelectionDropdowns();
  }

  // Method to override variant selection dropdowns
  overrideVariantSelectionDropdowns() {
    // Watch for variant selection dropdowns and ensure they only show available variants
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const dropdowns = node.querySelectorAll ? node.querySelectorAll('select[data-variant-select]') : [];
              dropdowns.forEach(dropdown => {
                this.filterDropdownOptions(dropdown);
              });
            }
          });
        }
      });
    });

    // Observe existing dropdowns
    const existingDropdowns = document.querySelectorAll('select[data-variant-select]');
    existingDropdowns.forEach(dropdown => {
      this.filterDropdownOptions(dropdown);
      observer.observe(dropdown, {
        childList: true,
        subtree: true
      });
    });

    // Observe for new dropdowns
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Method to filter dropdown options
  filterDropdownOptions(dropdown) {
    const variantDataScript = document.getElementById('product-options-variant-data');
    if (!variantDataScript) return;

    try {
      const productData = JSON.parse(variantDataScript.textContent);
      const options = dropdown.querySelectorAll('option');

      options.forEach(option => {
        const variantId = option.value;
        if (variantId) {
          const variantIndex = productData.variants.findIndex(v => v.id.toString() === variantId.toString());
          if (variantIndex !== -1) {
            const metafields = productData.variants_metafields[variantIndex];
            if (
              (metafields && metafields.custom && metafields.custom.availability === false) ||
              (metafields && metafields.availability === false)
            ) {
              // Hide unavailable variants
              option.style.display = 'none';
              option.disabled = true;
            } else {
              // Show available variants
              option.style.display = '';
              option.disabled = false;
            }
          }
        }
      });
    } catch (e) {
      // Error filtering dropdown options
    }
  }

  filterUnavailableOptions() {
    this.isProcessing = true;

    // SKIP shop collection items - they have their own handler
    const isShopCollectionPage = document.querySelector('.shop-collection__item');
    if (isShopCollectionPage) {
      this.isProcessing = false;
      return;
    }

    // New logic: parse product JSON and hide unavailable color swatches
    const variantDataScript = document.getElementById('product-options-variant-data');
    if (variantDataScript) {
      try {
        const productData = JSON.parse(variantDataScript.textContent);
        const colorOption = productData.options_with_values.find(opt => opt.name.toLowerCase() === 'color');
        const sizeOption = productData.options_with_values.find(opt => opt.name.toLowerCase() === 'size');
        const colorValues = colorOption ? colorOption.values.map(v => v.name) : [];
        // Find unavailable color values using variants_metafields
        let unavailableColors = [];
        if (productData.variants && productData.variants_metafields) {
          unavailableColors = productData.variants
            .map((variant, idx) => {
              const metafields = productData.variants_metafields[idx];
              if (
                (metafields && metafields.custom && metafields.custom.availability === false) ||
                (metafields && metafields.availability === false)
              ) {
                return (variant.option1 || '').toLowerCase().trim();
              }
              return null;
            })
            .filter(Boolean);
        }
        // Hide unavailable color swatches (existing logic, but only for .swatch--square)
        document.querySelectorAll('.product-options__option-group--color .product-options__option-value').forEach(optionValue => {
          // Only apply logic if this swatch contains a .swatch-input__label--square child
          if (!optionValue.querySelector('.swatch-input__label--square')) return;
          const input = optionValue.querySelector('input[type="radio"]');
          if (input) {
            const inputValue = (input.value || '').toLowerCase().trim();
            if (unavailableColors.includes(inputValue)) {
              optionValue.style.display = 'none';
              input.disabled = true;
            } else {
              optionValue.style.display = '';
              input.disabled = false;
            }
          }
        });

        // --- NEW LOGIC FOR SIZE OPTIONS ---
        // Find selected color
        let selectedColor = null;
        const colorInputs = document.querySelectorAll('.product-options__option-group--color input[type="radio"]');
        colorInputs.forEach(input => {
          if (input.checked) {
            selectedColor = input.value;
          }
        });
        // Dynamically determine color and size option indices
        const colorIndex = productData.options.findIndex(opt => opt.toLowerCase() === 'color');
        const sizeIndex = productData.options.findIndex(opt => opt.toLowerCase() === 'size');
        if (selectedColor && sizeOption && colorIndex !== -1 && sizeIndex !== -1) {
          // For each size option, check if a variant exists for selected color + size and if its metafield availability is false
          document.querySelectorAll('.product-options__option-group--size .product-options__option-value').forEach(optionValue => {
            const input = optionValue.querySelector('input[type="radio"], input[type="checkbox"]');
            if (input) {
              // Use data-option-value if present, otherwise fallback to input.value
              let sizeValue = optionValue.getAttribute('data-option-value') || input.value;
              sizeValue = (sizeValue || '').toLowerCase().trim();
              let normalizedSelectedColor = (selectedColor || '').toLowerCase().trim();

              // Find the variant for selected color + this size
              const variantIdx = productData.variants.findIndex(variant => {
                const colorMatch = ((variant[`option${colorIndex + 1}`] || '').toLowerCase().trim() === normalizedSelectedColor);
                const sizeMatch = ((variant[`option${sizeIndex + 1}`] || '').toLowerCase().trim() === sizeValue);
                return colorMatch && sizeMatch;
              });

              // Hide size if: (1) no variant exists for this color+size combo, OR (2) variant exists but metafield availability is false
              let unavailable = false;
              if (variantIdx === -1) {
                // No variant exists for selected color + this size → HIDE (don't show invalid combinations)
                unavailable = true;
              } else {
                const metafields = productData.variants_metafields[variantIdx];
                if (
                  (metafields && metafields.custom && metafields.custom.availability === false) ||
                  (metafields && metafields.availability === false)
                ) {
                  unavailable = true;
                }
              }
              if (unavailable) {
                optionValue.style.display = 'none';
                input.disabled = true;
              } else {
                optionValue.style.display = '';
                input.disabled = false;
              }
            }
          });
        } else if (sizeOption) {
          // If no color is selected, show all size options
          document.querySelectorAll('.product-options__option-group--size .product-options__option-value').forEach(optionValue => {
            const input = optionValue.querySelector('input[type="radio"], input[type="checkbox"]');
            if (input) {
              optionValue.style.display = '';
              input.disabled = false;
            }
          });
        }
        // --- END NEW LOGIC FOR SIZE OPTIONS ---
      } catch (e) {
        // Fallback to old logic if JSON parsing fails
        const optionGroups = document.querySelectorAll('.product-options__option-group');

        optionGroups.forEach(group => {
          const optionValues = group.querySelectorAll('.product-options__option-value');
          const optionName = group.querySelector('.product-options__option-group-legend')?.textContent.trim();

          optionValues.forEach(optionValue => {
            const availability = optionValue.getAttribute('data-availability');
            const hasAvailableVariant = optionValue.getAttribute('data-has-available-variant');
            const optionValueText = optionValue.getAttribute('data-option-value');

            if (availability === 'false' || hasAvailableVariant === 'false') {
              // Hide the option and all its children
              optionValue.style.display = 'none';
              optionValue.style.pointerEvents = 'none';
              optionValue.style.opacity = '0';
              optionValue.style.visibility = 'hidden';
              optionValue.style.position = 'absolute';
              optionValue.style.width = '0';
              optionValue.style.height = '0';
              optionValue.style.overflow = 'hidden';
              optionValue.style.clip = 'rect(0 0 0 0)';

              // Disable and hide all input elements inside
              const inputs = optionValue.querySelectorAll('input');
              inputs.forEach(input => {
                input.disabled = true;
                input.style.display = 'none';
                input.setAttribute('aria-hidden', 'true');
              });

              // Handle swatch-specific elements
              const swatches = optionValue.querySelectorAll('.swatch-input, .swatch, [class*="swatch"], .product-form__color-swatch');
              swatches.forEach(swatch => {
                swatch.style.display = 'none';
                swatch.style.pointerEvents = 'none';
                swatch.style.opacity = '0';
                swatch.style.visibility = 'hidden';
                swatch.style.position = 'absolute';
                swatch.style.width = '0';
                swatch.style.height = '0';
                swatch.style.overflow = 'hidden';
                swatch.style.clip = 'rect(0 0 0 0)';
                swatch.setAttribute('aria-hidden', 'true');
                swatch.setAttribute('tabindex', '-1');
              });

              // Also hide any labels or text elements
              const labels = optionValue.querySelectorAll('label');
              labels.forEach(label => {
                label.style.display = 'none';
                label.setAttribute('aria-hidden', 'true');
              });
            } else {
              // Show the option and all its children
              optionValue.style.display = '';
              optionValue.style.pointerEvents = '';
              optionValue.style.opacity = '';
              optionValue.style.visibility = '';
              optionValue.style.position = '';
              optionValue.style.width = '';
              optionValue.style.height = '';
              optionValue.style.overflow = '';
              optionValue.style.clip = '';

              // Re-enable and show all input elements inside
              const inputs = optionValue.querySelectorAll('input');
              inputs.forEach(input => {
                input.disabled = false;
                input.style.display = '';
                input.removeAttribute('aria-hidden');
              });

              // Handle swatch-specific elements
              const swatches = optionValue.querySelectorAll('.swatch-input, .swatch, [class*="swatch"], .product-form__color-swatch');
              swatches.forEach(swatch => {
                swatch.style.display = '';
                swatch.style.pointerEvents = '';
                swatch.style.opacity = '';
                swatch.style.visibility = '';
                swatch.style.position = '';
                swatch.style.width = '';
                swatch.style.height = '';
                swatch.style.overflow = '';
                swatch.style.clip = '';
                swatch.removeAttribute('aria-hidden');
                swatch.removeAttribute('tabindex');
              });

              // Show any labels or text elements
              const labels = optionValue.querySelectorAll('label');
              labels.forEach(label => {
                label.style.display = '';
                label.removeAttribute('aria-hidden');
              });
            }
          });
        });
      }
    }

    // Update legend values to show first available variant (debounced)
    this.debouncedUpdateLegendValues();

    // Mark processing as complete
    this.isProcessing = false;
  }

  // Debounced version of updateLegendValues to improve performance
  debouncedUpdateLegendValues() {
    if (this.legendUpdateTimeout) {
      clearTimeout(this.legendUpdateTimeout);
    }
    this.legendUpdateTimeout = setTimeout(() => {
      this.updateLegendValues();
    }, 50); // Small delay to batch multiple calls
  }

  updateLegendValues() {
    // Cache DOM queries to improve performance
    if (!this.optionGroupsCache || this.optionGroupsCache.length === 0) {
      this.optionGroupsCache = document.querySelectorAll('.product-options__option-group');
    }

    this.optionGroupsCache.forEach(group => {
      // Skip dropdown options completely - they are handled by dropdown-legend-handler.js
      if (group.hasAttribute('data-option-type') && group.getAttribute('data-option-type') === 'dropdown') {
        return;
      }

      // Also skip any legend elements that are within dropdown groups
      const legendValue = group.querySelector('.product-options__option-group-legend-value');
      if (!legendValue) return;

      // Double-check that this legend is not within a dropdown group
      if (legendValue.closest('.product-options__option-group[data-option-type="dropdown"]')) {
        return;
      }

      // Use more specific selector to reduce DOM traversal
      const visibleOptionValues = group.querySelectorAll('.product-options__option-value:not([style*="display: none"]):not([style*="visibility: hidden"])');
      let selectedValue = null;
      let firstAvailableValue = null;

      // Find the selected option value and first available option value
      for (let optionValue of visibleOptionValues) {
        const availability = optionValue.getAttribute('data-availability');
        const hasAvailableVariant = optionValue.getAttribute('data-has-available-variant');
        const optionValueText = optionValue.getAttribute('data-option-value');

        // Check if this option is available
        if (availability !== 'false' && hasAvailableVariant !== 'false') {

          // Check if this option is selected
          const input = optionValue.querySelector('input[type="radio"]:checked, input[type="checkbox"]:checked');
          if (input) {
            selectedValue = optionValueText;
            break;
          }

          // Store the first available value as fallback
          if (!firstAvailableValue) {
            firstAvailableValue = optionValueText;
          }
        }
      }

      // Update the legend value: show selected value if available, otherwise show first available value
      if (selectedValue) {
        legendValue.textContent = selectedValue;
      } else if (firstAvailableValue) {
        legendValue.textContent = firstAvailableValue;
      }
    });
  }

  setupEventListeners() {
    // Debounced version of filterUnavailableOptions and filterProductDataForTheme
    const debouncedFilter = this.debounce(() => {
      this.filterUnavailableOptions();
      this.filterProductDataForTheme();
    }, 100);

    // Listen for variant changes
    document.addEventListener('variant:changed', () => {
      debouncedFilter();
    });

    // Listen for theme's variant change events
    document.addEventListener('flu:product:variant-change', function (evt) {
      // Ensure the variant in the event is available
      if (evt.detail && evt.detail.variant) {
        const variant = evt.detail.variant;
        const variantDataScript = document.getElementById('product-options-variant-data');
        if (variantDataScript) {
          try {
            const productData = JSON.parse(variantDataScript.textContent);
            const variantIndex = productData.variants.findIndex(v => v.id === variant.id);
            if (variantIndex !== -1) {
              const metafields = productData.variants_metafields[variantIndex];
              if (
                (metafields && metafields.custom && metafields.custom.availability === false) ||
                (metafields && metafields.availability === false)
              ) {
                // Find the first available variant instead
                const availableVariant = productData.variants.find((v, idx) => {
                  const vMetafields = productData.variants_metafields[idx];
                  return !(
                    (vMetafields && vMetafields.custom && vMetafields.custom.availability === false) ||
                    (vMetafields && vMetafields.availability === false)
                  );
                });

                if (availableVariant) {
                  // Update the event with the available variant
                  evt.detail.variant = availableVariant;
                }
              }
            }
          } catch (e) {
            // Error checking variant availability in event
          }
        }
      }
      debouncedFilter();
    });

    // Listen for option changes
    const productForm = document.querySelector('form[data-product-form]');
    if (productForm) {
      const optionInputs = productForm.querySelectorAll('select[name^="options["], input[type="radio"][name^="options["]');
      optionInputs.forEach(input => {
        input.addEventListener('change', () => {
          debouncedFilter();
        });
      });
    }

    // Listen for swatch clicks
    document.addEventListener('click', (event) => {
      const swatch = event.target.closest('.swatch-input, .swatch, [class*="swatch"], .product-form__color-swatch');
      if (swatch) {
        debouncedFilter();
      }
    });

    // Listen for AJAX content updates
    document.addEventListener('ajax:complete', () => {
      debouncedFilter();
    });
    document.addEventListener('ajax:success', () => {
      debouncedFilter();
    });

    // Listen for custom events that might be triggered by the furniture_new template
    document.addEventListener('furniture:variant-updated', () => {
      debouncedFilter();
    });

    // Listen for DOM content loaded
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(() => {
        this.filterUnavailableOptions();
        this.filterProductDataForTheme();
      }, 100);
    });

    // Listen for window load
    window.addEventListener('load', () => {
      setTimeout(() => {
        this.filterUnavailableOptions();
        this.filterProductDataForTheme();
      }, 100);
    });

    // Listen for any dynamic content changes
    const observer = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          // Only update if the changes are relevant to product options
          if (mutation.target.closest('.product-options') ||
            mutation.target.matches('.product-options__option-value') ||
            mutation.target.matches('.product-options__option-group')) {
            shouldUpdate = true;
          }
        }
      });
      if (shouldUpdate) {
        debouncedFilter();
      }
    });

    // Observe the entire document for changes
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true
    });
  }

  // Utility method for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  setupMutationObserver() {
    // Watch for changes in the product options container
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          setTimeout(() => {
            this.filterUnavailableOptions();
            this.filterProductDataForTheme();
          }, 0);
        }
      });
    });

    const productOptions = document.querySelector('.product-options');
    if (productOptions) {
      observer.observe(productOptions, {
        childList: true,
        subtree: true,
        attributes: true
      });
    }
  }

  // Method to ensure proper initial variant selection when first variant is unavailable
  ensureProperInitialVariantSelection() {
    const variantDataScript = document.getElementById('product-options-variant-data');
    if (!variantDataScript) return;

    try {
      const productData = JSON.parse(variantDataScript.textContent);
      const urlVariantFromSearch = window.__INITIAL_VARIANT_FROM_URL || new URLSearchParams(window.location.search).get('variant');
      const urlVariantId = urlVariantFromSearch ? parseInt(urlVariantFromSearch, 10) : null;
      const matchedUrlVariant = Number.isFinite(urlVariantId)
        ? productData.variants.find(v => v.id === urlVariantId)
        : null;
      const isUrlVariantValid = !!matchedUrlVariant;
      console.log('[VariantAvailability][InitSelection] URL Variant:', urlVariantId, 'Matched Variant:', matchedUrlVariant, 'isValid:', isUrlVariantValid);

      // Use initial URL variant (captured at page load) - URL may be stripped by View Transitions / section fetch
      const variantFromUrl = window.__INITIAL_VARIANT_FROM_URL || new URLSearchParams(window.location.search).get('variant');
      if (variantFromUrl) {
        const variantId = parseInt(variantFromUrl, 10);
        if (!isNaN(variantId)) {
          const urlVariant = productData.variants.find(v => v.id === variantId);
          if (urlVariant) {
            const idx = productData.variants.indexOf(urlVariant);
            const metafields = productData.variants_metafields?.[idx];
            const isAvailable = !(
              (metafields && metafields.custom && metafields.custom.availability === false) ||
              (metafields && metafields.availability === false)
            );
            console.log('[VariantAvailability][InitSelection] URL variant availability check:', {
              variantId,
              isAvailable,
              metafields
            });
            if (isAvailable) return;
          }
        }
      }

      if (isUrlVariantValid) {
        console.log('[VariantAvailability][InitSelection] Guard active: preserving valid URL variant, skipping fallback to first available');
        return;
      }

      // Find the first available variant
      const firstAvailableVariant = productData.variants.find((variant, idx) => {
        const metafields = productData.variants_metafields[idx];
        return !(
          (metafields && metafields.custom && metafields.custom.availability === false) ||
          (metafields && metafields.availability === false)
        );
      });

      if (firstAvailableVariant) {
        // Update the variant ID input to the first available variant
        const variantIdInput = document.querySelector('input[name="id"], select[name="id"]');
        if (variantIdInput) {
          console.log('[VariantAvailability][InitSelection] Before override:', variantIdInput.value, 'After override:', firstAvailableVariant.id);
          variantIdInput.value = firstAvailableVariant.id;
        }

        // Also update any hidden variant select elements
        const variantSelects = document.querySelectorAll('select[name="id"]');
        variantSelects.forEach(select => {
          const option = select.querySelector(`option[value="${firstAvailableVariant.id}"]`);
          if (option) {
            select.value = firstAvailableVariant.id;
          }
        });

        // Trigger a custom event to notify other scripts
        document.dispatchEvent(new CustomEvent('variant:initial-selection', {
          detail: { variant: firstAvailableVariant }
        }));
      }
    } catch (e) {
      // Error ensuring proper initial variant selection
    }
  }

  // Method to coordinate with dropdown-legend-handler
  coordinateWithDropdownLegendHandler() {
    // Wait for dropdown-legend-handler to be available
    setTimeout(() => {
      if (window.dropdownLegendHandler) {
        // Ensure proper initial selection before dropdown handler runs
        this.ensureProperInitialVariantSelection();

        // Update dropdown legends after a delay
        setTimeout(() => {
          if (window.dropdownLegendHandler.updateDropdownLegends) {
            window.dropdownLegendHandler.updateDropdownLegends();
          }
        }, 100);
      }
    }, 200);
  }

  // Global fix to prevent toString error
  preventToStringError() {
    // Override the getOptionValue method globally to prevent undefined values
    if (window.VariantSelect && window.VariantSelect.prototype) {
      const originalGetOptionValue = window.VariantSelect.prototype.getOptionValue;
      if (originalGetOptionValue) {
        window.VariantSelect.prototype.getOptionValue = function (fieldset) {
          const result = originalGetOptionValue.call(this, fieldset);
          // Return empty string instead of undefined to prevent toString error
          return result !== undefined && result !== null ? result : '';
        };
      }
    }

    // Also patch any existing instances
    const variantSelectElements = document.querySelectorAll('[variant-select]');
    variantSelectElements.forEach(element => {
      if (element._variantSelectDirective && element._variantSelectDirective.getOptionValue) {
        const originalGetOptionValue = element._variantSelectDirective.getOptionValue;
        element._variantSelectDirective.getOptionValue = function (fieldset) {
          const result = originalGetOptionValue.call(this, fieldset);
          return result !== undefined && result !== null ? result : '';
        };
      }
    });
  }
}

// Create a global instance and expose the filter function
window.variantAvailability = new VariantAvailability();
window.filterUnavailableOptions = () => window.variantAvailability.filterUnavailableOptions();

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => window.variantAvailability);
} else {
  window.variantAvailability;
}